import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CheckroomRoundedIcon from '@mui/icons-material/CheckroomRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import FeedRoundedIcon from '@mui/icons-material/FeedRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { School } from 'models/school';
import { KIND_SERVER_TO_CLIENT_MAPPING } from 'consts/school';
import { getAccessibleBrandColor, mixBrandColor, resolveSchoolPublicSite } from 'helpers/publicSite';
import SquadInTouchLogo from 'components/SquadInTouchLogo/SquadInTouchLogo';
import CookieConsent from 'components/CookieConsent';
import { StorageHelper } from 'helpers/storage';
import { getAuthenticatedAppLink } from 'helpers/tournament';
import './SchoolPublicLayout.scss';
import FollowerRegistration from 'components/FollowerRegistration/FollowerRegistration';

interface Props {
   school: School;
}

interface SchoolNavigationItem {
   label: string;
   path: string;
   icon: React.ReactElement;
   isVisible: boolean;
   isComingSoon?: boolean;
}

function getInitials(name: string) {
   return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('') || 'S';
}

export default function SchoolPublicLayout({ school }: Props) {
   const [isMenuOpen, setIsMenuOpen] = useState(false);
   const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
   const location = useLocation();
   const settings = useMemo(() => resolveSchoolPublicSite(school), [school]);
   const accessibleBrand = getAccessibleBrandColor(settings.appearance.brandColor);
   const isCookiesAccepted = StorageHelper.LocalStorage.get('isCookiesAccepted');
   const isUnion = school.kind === KIND_SERVER_TO_CLIENT_MAPPING.SCHOOL_UNION;

   const navigation = useMemo<SchoolNavigationItem[]>(() => (isUnion ? [
      { label: 'Home', path: '/', icon: <HomeRoundedIcon />, isVisible: true },
      { label: 'Calendar', path: '/calendar', icon: <CalendarMonthRoundedIcon />, isVisible: settings.pages.calendar },
      { label: 'Tournaments', path: '/upcomingTournaments', icon: <EmojiEventsRoundedIcon />, isVisible: settings.pages.tournaments },
      { label: 'Results', path: '/tournamentResults', icon: <BarChartRoundedIcon />, isVisible: settings.pages.tournamentResults },
      { label: 'Roll of honour', path: '/records', icon: <WorkspacePremiumRoundedIcon />, isVisible: settings.pages.records },
      { label: 'Member schools', path: '/memberSchools', icon: <GroupsRoundedIcon />, isVisible: settings.pages.memberSchools },
      { label: 'Venues', path: '/venues', icon: <LocationOnRoundedIcon />, isVisible: settings.pages.venues },
      { label: 'Photos', path: '/photos', icon: <PhotoCameraRoundedIcon />, isVisible: settings.pages.photos },
      { label: 'News', path: '/news', icon: <FeedRoundedIcon />, isVisible: settings.pages.news },
      { label: 'Resources', path: '/files', icon: <FormatListBulletedRoundedIcon />, isVisible: settings.pages.resources },
   ] : [
      { label: 'Home', path: '/', icon: <HomeRoundedIcon />, isVisible: settings.pages.home },
      { label: 'Calendar', path: '/calendar', icon: <CalendarMonthRoundedIcon />, isVisible: settings.pages.calendar },
      { label: 'Fixtures', path: '/fixtures', icon: <FormatListBulletedRoundedIcon />, isVisible: settings.pages.fixturesAndResults },
      { label: 'Results', path: '/results', icon: <BarChartRoundedIcon />, isVisible: settings.pages.fixturesAndResults },
      { label: 'Teams', path: '/teams', icon: <CheckroomRoundedIcon />, isVisible: settings.pages.teams, isComingSoon: true },
      { label: 'Records', path: '/records', icon: <WorkspacePremiumRoundedIcon />, isVisible: settings.pages.records },
      { label: 'Photos', path: '/photos', icon: <PhotoCameraRoundedIcon />, isVisible: settings.pages.photos },
      { label: 'Tournaments', path: '/tournaments', icon: <EmojiEventsRoundedIcon />, isVisible: settings.pages.tournaments && school.isShowTournamentsOnPublicSite !== false },
      { label: 'Leagues', path: '/leagues', icon: <EmojiEventsRoundedIcon />, isVisible: settings.pages.leagues },
      { label: 'Challenges', path: '/challenges', icon: <WorkspacePremiumRoundedIcon />, isVisible: settings.pages.challenges },
      { label: 'Club events', path: '/clubEvents', icon: <GroupsRoundedIcon />, isVisible: settings.pages.clubEvents },
      { label: 'News', path: '/news', icon: <FeedRoundedIcon />, isVisible: settings.pages.news },
      { label: 'Search', path: '/search', icon: <SearchRoundedIcon />, isVisible: settings.pages.search, isComingSoon: true },
   ]).filter(item => item.isVisible), [isUnion, school.isShowTournamentsOnPublicSite, settings.pages]);

   useEffect(() => {
      setIsMenuOpen(false);
      window.scrollTo({ top: 0, behavior: 'auto' });
   }, [location.pathname]);

   useEffect(() => {
      const previousTitle = document.title;
      const existingRobots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
      const robots = existingRobots || document.createElement('meta');
      const previousRobots = existingRobots?.content;
      const existingDescription = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      const previousDescription = existingDescription?.content;
      const activePage = navigation.find(item => item.path === location.pathname)?.label;

      document.title = `${activePage && activePage !== 'Home' ? `${activePage} · ` : ''}${school.name}${isUnion ? '' : ' sport'}`;
      robots.name = 'robots';
      robots.content = settings.isSearchEngineIndexingEnabled && settings.status === 'PUBLIC_AVAILABLE'
         ? 'index, follow'
         : 'noindex, nofollow';
      if (!existingRobots) document.head.appendChild(robots);
      if (existingDescription) {
         const description = String(school.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
         existingDescription.content = description || (isUnion
            ? `Tournaments, results and member information from ${school.name}.`
            : `Fixtures, results and school sport updates from ${school.name}.`);
      }

      return () => {
         document.title = previousTitle;
         if (existingRobots) existingRobots.content = previousRobots || '';
         else robots.remove();
         if (existingDescription) existingDescription.content = previousDescription || '';
      };
   }, [isUnion, location.pathname, navigation, school.description, school.name, settings.isSearchEngineIndexingEnabled, settings.status]);

   useEffect(() => {
      if (!isMenuOpen) return;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      mobileCloseButtonRef.current?.focus();
      return () => { document.body.style.overflow = previousOverflow; };
   }, [isMenuOpen]);

   useEffect(() => {
      const onKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            setIsMenuOpen(false);
         }
      };

      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   }, []);

   const onLoginClick = () => {
      window.open(getAuthenticatedAppLink('/login'), '_blank', 'noopener,noreferrer');
   };

   const cssVariables = {
      '--sc-brand': accessibleBrand,
      '--sc-brand-soft': mixBrandColor(accessibleBrand, '#ffffff', 0.88),
      '--sc-brand-muted': mixBrandColor(accessibleBrand, '#ffffff', 0.68),
      '--sc-brand-deep': mixBrandColor(accessibleBrand, '#000000', 0.18),
   } as React.CSSProperties;

   const renderNavigation = () => (
      <div className='school-site-navigation' aria-label='School site navigation'>
         {navigation.map(item => (
            <NavLink
               key={item.path}
               to={item.path}
               end={item.path === '/'}
               className={({ isActive }) => `school-site-navigation__link${isActive ? ' is-active' : ''}`}
            >
               <span className='school-site-navigation__icon' aria-hidden='true'>{item.icon}</span>
               <span>{item.label}</span>
               {item.isComingSoon && <span className='school-site-navigation__soon'>Soon</span>}
            </NavLink>
         ))}
      </div>
   );

   return (
      <div className='school-site' style={cssVariables}>
         <a className='school-site__skip-link' href='#main-content'>Skip to content</a>

         <aside className={`school-site-sidebar${isMenuOpen ? ' is-open' : ''}`}>
            <div className='school-site-sidebar__identity'>
               <div className='school-site-sidebar__crest'>
                  {school.pic
                     ? <img src={school.pic} alt={`${school.name} crest`} />
                     : <span aria-hidden='true'>{getInitials(school.name)}</span>
                  }
               </div>
               <div className='school-site-sidebar__name'>{school.name}</div>
               <button
                  ref={mobileCloseButtonRef}
                  type='button'
                  className='school-site-sidebar__close'
                  aria-label='Close navigation'
                  onClick={() => setIsMenuOpen(false)}
               >
                  <CloseRoundedIcon />
               </button>
            </div>

            {renderNavigation()}

            <div className='school-site-sidebar__actions'>
               <NavLink className='school-site-sidebar__subscribe' to='/subscribe'>
                  <NotificationsNoneRoundedIcon aria-hidden='true' />
                  <span>Subscribe</span>
               </NavLink>
               <button type='button' className='school-site-sidebar__sign-in' onClick={onLoginClick}>
                  <LoginRoundedIcon aria-hidden='true' />
                  Sign in
               </button>
               <FollowerRegistration school={school} />
            </div>
         </aside>

         {isMenuOpen && (
            <button
               type='button'
               className='school-site__scrim'
               aria-label='Close navigation'
               onClick={() => setIsMenuOpen(false)}
            />
         )}

         <div className='school-site__body'>
            <header className='school-site-mobile-header'>
               <div className='school-site-mobile-header__identity'>
                  <div className='school-site-mobile-header__crest'>
                     {school.pic
                        ? <img src={school.pic} alt='' />
                        : <span aria-hidden='true'>{getInitials(school.name)}</span>
                     }
                  </div>
                  <span>{school.name}</span>
               </div>
               <button
                  type='button'
                  className='school-site-mobile-header__menu'
                  aria-label='Open navigation'
                  aria-expanded={isMenuOpen}
                  onClick={() => setIsMenuOpen(true)}
               >
                  <MenuRoundedIcon />
               </button>
            </header>

            <div id='main-content' className='school-site__main'>
               <Outlet />
            </div>

            <footer className='school-site-footer'>
               <div className='school-site-footer__school'>
                  <strong>{school.name}</strong>
                  {school.address && <span>{school.address.trim()}</span>}
               </div>
               <div className='school-site-footer__powered'>
                  <SquadInTouchLogo />
                  <span>Powered by Squad In Touch</span>
               </div>
            </footer>
         </div>

         {!isCookiesAccepted && <CookieConsent />}
      </div>
   );
}
